// src/pages/MentorDashboard.js
import React, { useEffect, useState } from 'react';
import axios from '../axiosInstance';
import { Link, useNavigate } from 'react-router-dom';
import ChatPopup from '../components/ChatPopup';
import JitsiMeet from '../components/JitsiMeet';
// Hinweis: Button ist aktuell nicht genutzt, kann bei Bedarf entfernt werden
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import MenteeCalendar from '../components/MenteeCalendar';
import MentoringJournal from '../components/MentoringJournal';
import MentoringAgreementView from '../components/MentoringAgreement/MentoringAgreementView';
import MentorFeedbackHistory from './Feedback/MentorFeedbackHistory';
import MenteeMatchInfo from '../components/MenteeMatchInfo';
import { useTranslation } from 'react-i18next';
import { usePairing } from '../context/PairingContext';

/* --------------------------- kleine Helfer --------------------------- */
function get(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] != null) return obj[k];
  }
  return undefined;
}
function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ⚙️ Neu: Mentee-Daten für die Card normalisieren (Bildpfad/Base64) */
function normalizeMenteeForCard(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  const firstName = raw.firstName ?? raw.first_name ?? '';
  const lastName  = raw.lastName  ?? raw.last_name  ?? '';

  const avatarPath =
    (raw.avatarPath ?? raw.avatar_path ?? '').toString().replace(/\\/g, '/');

  const avatarDataUrl = raw.avatar ? `data:image/png;base64,${raw.avatar}` : null;

  // ✅ NEU (die einzige Änderung): Beschreibung robust mappen,
  // damit das Popup in MenteeMatchInfo den Text anzeigen kann.
  const profileDescriptionRaw =
    raw.profileDescription ??
    raw.profile_description ??
    raw.why_mentoring ??
    raw.about ??
    raw.description ??
    raw.bio ??
    '';

  const profileDescription =
    (profileDescriptionRaw ?? '').toString().trim();

  return {
    ...raw,
    firstName,
    lastName,
    avatarPath,
    remoteImageBase64: avatarDataUrl,
    avatarDataUrl,

    // ✅ für unterschiedliche Komponenten-Keys beide Varianten anbieten
    profileDescription,
    profile_description: raw.profile_description ?? profileDescription,
    surveyId:          raw.survey_id || null,
    applicationFields: raw.application_fields || [],
    methodsSkills:     raw.methods_skills || [],
  };
}

export default function MentorDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedMatchingId } = usePairing();

  const API_URL =
    process.env.REACT_APP_API_URL_MENTORING || process.env.REACT_APP_API_URL || '';

  const [user, setUser] = useState(null);
  const [mentee, setMentee] = useState(null);

  // 'mentee' = Chat mit Mentee, 'hr' = Chat mit HR, null = kein Chat offen
  const [chatOpen, setChatOpen] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const [agreement, setAgreement] = useState(null);
  const [activeTab, setActiveTab] = useState('vereinbarung');
  const [dueFeedback, setDueFeedback] = useState(null);

  // Konto löschen – Modal (wie im MenteeDashboard)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState('info');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Bestätigungswort abhängig von Sprache
  const isGerman = String(i18n.language || 'de').toLowerCase().startsWith('de');
  const deleteKeyword = isGerman ? 'LÖSCHEN' : 'DELETE';
  const deleteKeywordAlt = isGerman ? 'LOESCHEN' : null;
  const confirmUpper = deleteConfirmText.trim().toUpperCase();
  const canDelete = isGerman
    ? confirmUpper === deleteKeyword || confirmUpper === deleteKeywordAlt
    : confirmUpper === deleteKeyword;

  // Badge-Zähler
  const [unreadFromHR, setUnreadFromHR] = useState(0);
  const [menteeToMentorUnreadCount, setMenteeToMentorUnreadCount] = useState(0);

  const jitsiLang = (i18n.language || 'de').split('-')[0];

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteStep('info');
    setDeleteConfirmText('');
  };

  // ✅ NEU: Hard-Redirect zur Login-Seite (robust, auch wenn Session schon weg ist)
  const hardRedirectToLogin = () => {
    const hash = window.location.hash || '';
    const q = hash.includes('?') ? hash.slice(hash.indexOf('?')) : '';
    navigate(`/login${q}`);
    window.location.assign(`/#/login${q}`);
  };

  const handleDeactivateAccount = async () => {
    if (!user?.user_id) return;

    try {
      await axios.post(
        `${API_URL}/mentoring/account/delete-request`,
        {},
        { withCredentials: true }
      );

      closeDeleteModal();

      alert(
        t(
          'mentorDashboard.deleteAccountDeactivatedInfo',
          'Dein Konto wurde deaktiviert. HR kann dir bei Bedarf einen Undo-Link schicken.'
        )
      );

      // ✅ NEU: Nach erfolgreichem Request (Session destroy) zuverlässig raus
      hardRedirectToLogin();
    } catch (err) {
      const status = err?.response?.status;

      // ✅ NEU: Wenn Session schon weg/abgelaufen ist, darf das Dashboard nicht stehen bleiben
      if (status === 401) {
        closeDeleteModal();
        alert(
          t(
            'mentorDashboard.sessionExpired',
            'Deine Session ist abgelaufen. Bitte logge dich erneut ein.'
          )
        );
        hardRedirectToLogin();
        return;
      }

      console.error('Deaktivierung fehlgeschlagen:', err);
      alert(
        t(
          'mentorDashboard.somethingWentWrong',
          'Etwas ist schiefgelaufen. Bitte später erneut versuchen.'
        )
      );
    }
  };

  /* --------------------------- Laden: Profil --------------------------- */
  useEffect(() => {
    let stop = false;

    axios
      .get(`${API_URL}/mentoring/mentor/profile`, { withCredentials: true })
      .then((res) => {
        if (stop) return;
        setUser(res.data);
      })
      .catch((err) => {
        console.error('❌ Fehler beim Laden des Mentorenprofils:', err);
      });

    return () => {
      stop = true;
    };
  }, [API_URL]);

  /* ---------------------- Laden: Gematchter Mentee --------------------- */
  useEffect(() => {
    if (!user) {
      setMentee(null);
      return;
    }
    const uid =
      get(user, 'user_id', 'id', 'mentor_id') ??
      get(user?.user, 'id', 'user_id');

    if (!uid) {
      setMentee(null);
      return;
    }

    let stop = false;

    axios
      .get(`${API_URL}/mentoring/mentor/${uid}/matched-mentees${selectedMatchingId ? `?matching_id=${selectedMatchingId}` : ''}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (stop) return;
        let menteeObj = res?.data ?? null;
        if (Array.isArray(menteeObj)) menteeObj = menteeObj[0] || null;
        /* ✨ Neu: Mentee normalisieren, damit Bild zuverlässig angezeigt wird */
        const normalized = menteeObj ? normalizeMenteeForCard(menteeObj) : null;
        setMentee(normalized);
      })
      .catch((err) => {
        console.error('Fehler beim Laden des gematchten Mentees:', err);
        setMentee(null);
      });

    return () => {
      stop = true;
    };
  }, [user, API_URL, selectedMatchingId]);

  /* ---------------------- Laden: Unread-Badges ------------------------ */
  useEffect(() => {
    let stop = false;

    const fetchUnread = async () => {
      try {
        // 1) Mentee→Mentor (Badge in der Mentee-Karte) – gezielt für das Paar
        const menteeId  = Number(get(mentee || {}, 'user_id','id','mentee_id'));
        const mentorId  = Number(get(user   || {}, 'user_id','id','mentor_id'));
        let menteeUnreadCount = 0;
        if (mentorId && menteeId) {
          const r1 = await axios.get(
            `${API_URL}/mentoring/mentor-to-mentee`,
            { params: { mentorId, menteeId }, withCredentials: true }
          );
          menteeUnreadCount = Number(r1?.data?.unread || 0);
        }

        // 2) HR→Mentor (Badge beim HR-Icon) – Aggregat bleibt
        let hrCount = 0;
        const r2 = await axios.get(
          `${API_URL}/mentoring/chat/unread/mentor/all`,
          { withCredentials: true }
        );
        const rows = Array.isArray(r2?.data) ? r2.data : [];
        const hrRow = rows.find(e => String(e.sender_role).toLowerCase() === 'hr');
        hrCount = hrRow ? parseInt(hrRow.unread_count ?? hrRow.unread, 10) || 0 : 0;

        if (stop) return;
        setMenteeToMentorUnreadCount(menteeUnreadCount);
        setUnreadFromHR(hrCount);
      } catch {
        if (!stop) {
          setMenteeToMentorUnreadCount(0);
          setUnreadFromHR(0);
        }
      }
    };

    fetchUnread();
    const id = setInterval(fetchUnread, 10_000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [API_URL, user, mentee]);

  /* ➕ Neu: Paar-spezifischen Unread-Count (Mentee → Mentor) laden */
  useEffect(() => {
    const mentorId = asNumber(get(user || {}, 'user_id', 'id', 'mentor_id'));
    const menteeId = asNumber(get(mentee || {}, 'user_id', 'id', 'mentee_id'));
    if (!mentorId || !menteeId) return;

    let stop = false;

    const fetchPairUnread = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/mentoring/mentor-to-mentee`,
          {
            withCredentials: true,
            params: { mentorId, menteeId },
          }
        );
        if (stop) return;
        const n = Number(res.data?.unread ?? res.data?.count ?? 0);
        setMenteeToMentorUnreadCount(Number.isFinite(n) ? n : 0);
      } catch {
        if (!stop) setMenteeToMentorUnreadCount(0);
      }
    };

    fetchPairUnread();
    const id = setInterval(fetchPairUnread, 10_000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [user, mentee, API_URL]);

  /* ----------- Laden: Vereinbarung & offenes Feedback ----------------- */
  useEffect(() => {
    const uid = get(user || {}, 'user_id', 'id');
    if (!uid) return;

    let stop = false;

    axios
      .get(`${API_URL}/mentoring/agreement/mentor/${uid}${selectedMatchingId ? `?matching_id=${selectedMatchingId}` : ''}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (stop) return;
        setAgreement(res.data || null);
      })
      .catch(() => {
        if (!stop) setAgreement(null);
      });

    axios
      .get(`${API_URL}/mentoring/feedback/status/${uid}${selectedMatchingId ? `?matching_id=${selectedMatchingId}` : ''}`, {
        withCredentials: true,
      })
      .then((res) => {
        if (stop) return;
        if (res?.data?.open) setDueFeedback({ milestone: res.data.milestone });
        else setDueFeedback(null);
      })
      .catch(() => {
        if (!stop) setDueFeedback(null);
      });

    return () => {
      stop = true;
    };
  }, [user, API_URL, selectedMatchingId]);

  /* ------------------- Beim Öffnen: als gelesen markieren ------------- */
  useEffect(() => {
    if (!chatOpen || !user) return;

    const menteeId = asNumber(get(mentee || {}, 'user_id', 'id', 'mentee_id'));
    const mentorId = asNumber(get(user || {}, 'user_id', 'id', 'mentor_id'));

    const run = async () => {
      try {
        if (chatOpen === 'hr') {
          // HR-Thread: bevorzugt HR-spezifischen Endpoint
          try {
            await axios.patch(
              `${API_URL}/mentoring/messages/mark-as-read/hr`,
              { menteeId: menteeId ?? null, mentorId: mentorId ?? null, role: 'mentor' },
              { withCredentials: true }
            );
          } catch {
            // Fallback: allgemeiner Endpoint mit Channel
            await axios.patch(
              `${API_URL}/mentoring/messages/mark-as-read`,
              {
                menteeId: menteeId ?? null,
                mentorId: mentorId ?? null,
                role: 'mentor',
                channel: 'hr',
              },
              { withCredentials: true }
            );
          }
          setUnreadFromHR(0);
        } else if (chatOpen === 'mentee' && menteeId && mentorId) {
          await axios.patch(
            `${API_URL}/mentoring/messages/mark-as-read`,
            { menteeId, mentorId, role: 'mentor' },
            { withCredentials: true }
          );
          setMenteeToMentorUnreadCount(0);
        }
      } catch {
        // still tolerieren
      }
    };

    run();
  }, [chatOpen, user, mentee, API_URL]);

  /* ---------------------------- Render -------------------------------- */

  if (!user) {
    return (
      <div className="text-white p-6">
        {t('mentorDashboard.loadingUserData', 'Lade Mentorendaten...')}
      </div>
    );
  }

  // Namen/Felder robust mappen
  const userId = asNumber(get(user, 'user_id', 'id', 'mentor_id'));
  const firstName = get(user, 'first_name', 'firstName') || '';
  const lastName = get(user, 'last_name', 'lastName') || '';

  const menteeId = asNumber(get(mentee || {}, 'user_id', 'id', 'mentee_id'));
  const menteeFirst = get(mentee || {}, 'first_name', 'firstName') || '';
  const menteeLast = get(mentee || {}, 'last_name', 'lastName') || '';

  return (
    <div className="min-h-screen bg-black text-white p-6 font-sans">
      {/* Kopfzeile */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex gap-4">
          <Link
            to="/mentor-profile"
            className="bg-mentixxgreen hover:bg-green-500 focus:ring-2 focus:ring-mentixxgreen text-black font-bold py-2 px-5 rounded-2xl shadow transition"
          >
            {t('mentorDashboard.profile', 'Mentor-Profil')}
          </Link>
          <a
            href="https://greentechtalents.oncademy.de/users/sign_in"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-mentixxgreen hover:bg-green-500 focus:ring-2 focus:ring-mentixxgreen text-black font-bold py-2 px-5 rounded-2xl shadow transition"
          >
            {t('mentorDashboard.elearning', 'E-Learning Akademie')}
          </a>
        </nav>

        <div className="flex items-center gap-4">
          {/* HR-Chat Badge */}
          <div className="relative">
            <button
              onClick={() => setChatOpen('hr')}
              className="relative text-white hover:text-mentixxgreen"
              title={t('mentorDashboard.hrChatOpen', 'HR-Chat öffnen')}
            >
              💬
              {unreadFromHR > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {unreadFromHR}
                </span>
              )}
            </button>
          </div>

          {/* Konto löschen */}
          <Button
            onClick={() => setDeleteModalOpen(true)}
            className="bg-red-500 text-white py-2 px-4 rounded-full"
          >
            {t('mentorDashboard.deleteAccount', 'Konto löschen')}
          </Button>
        </div>
      </div>

      {/* Konto löschen – Modal */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(open) => {
          if (!open) closeDeleteModal();
          else setDeleteModalOpen(true);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('mentorDashboard.deleteAccount', 'Konto löschen')}</DialogTitle>
          </DialogHeader>

          {deleteStep === 'info' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                {t('mentorDashboard.deleteInfoLine1', 'Wenn du dein Konto löschst, wird es zunächst deaktiviert.')}
              </p>
              <p className="text-sm text-gray-700">
                {t('mentorDashboard.deleteInfoLine2', 'Deine Daten werden nach einer Frist endgültig entfernt. Bis dahin kann HR dir einen Undo-Link schicken.')}
              </p>
              <p className="text-sm text-gray-700">
                {t('mentorDashboard.deleteInfoLine3', 'Falls du aktuell gematcht bist, wird die Mentoring-Beziehung beendet.')}
              </p>

              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteModal}>
                  {t('mentorDashboard.cancel', 'Abbrechen')}
                </Button>
                <Button onClick={() => setDeleteStep('confirm')}>
                  {t('mentorDashboard.continue', 'Weiter')}
                </Button>
              </div>
            </div>
          )}

          {deleteStep === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                {isGerman ? (
                  <>
                    Bitte tippe <strong>{deleteKeyword}</strong>, um die Deaktivierung zu bestätigen.
                  </>
                ) : (
                  <>
                    Please type <strong>{deleteKeyword}</strong> to confirm deactivation.
                  </>
                )}
              </p>

              <input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteKeyword}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-black"
              />

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteStep('info')}>
                  {t('mentorDashboard.back', 'Zurück')}
                </Button>

                <Button
                  className="bg-red-600 text-white"
                  disabled={!canDelete}
                  onClick={handleDeactivateAccount}
                >
                  {t('mentorDashboard.deactivateNow', 'Jetzt deaktivieren')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Obere Sektion */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Mentee-Profil */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <h2 className="font-bold mb-2">
            {t('mentorDashboard.menteeProfile', 'Mentee-Profil')}
          </h2>
          {mentee ? (
            <MenteeMatchInfo
              mentee={mentee}
              unreadCount={menteeToMentorUnreadCount}
              onSendMessage={() => setChatOpen('mentee')}
              onStartVideoCall={() => setVideoOpen(true)}
            />
          ) : (
            <div className="text-gray-400">
              {t('mentorDashboard.noMentee', 'Noch kein Mentee zugewiesen')}
            </div>
          )}
        </div>

        {/* Kalender */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <h2 className="font-bold mb-2">
            {t('mentorDashboard.calendar', 'Kalender')}
          </h2>
          <MenteeCalendar />
        </div>
      </div>

      {/* Untere Sektion */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Journal */}
        <div className="bg-gray-800 p-6 rounded-2xl shadow flex flex-col min-h-[450px]">
          {user && <MentoringJournal user={user} viewerRole="mentor" />}
        </div>

        {/* Vereinbarung & Feedback */}
        <div className="bg-gray-800 p-6 rounded-2xl shadow flex flex-col justify-start min-h-[450px] text-center">
          <h2 className="font-bold text-white text-left text-base mb-3">
            {t('mentorDashboard.agreementAndFeedback', 'Mentoringvereinbarung & Feedback')}
          </h2>

          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setActiveTab('vereinbarung')}
              className={`px-4 py-1 rounded-full text-sm font-medium ${
                activeTab === 'vereinbarung'
                  ? 'bg-mentixxgreen text-black'
                  : 'bg-gray-700 text-white'
              }`}
            >
              {t('mentorDashboard.agreement', 'Vereinbarung')}
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`relative px-4 py-1 rounded-full text-sm font-medium ${
                activeTab === 'feedback'
                  ? 'bg-mentixxgreen text-black'
                  : 'bg-gray-700 text-white'
              }`}
            >
              {t('mentorDashboard.feedback', 'Feedback')}
              {dueFeedback && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  !
                </span>
              )}
            </button>
          </div>

          {activeTab === 'vereinbarung' && agreement ? (
            <MentoringAgreementView
              mentorName={`${firstName} ${lastName}`.trim()}
              menteeName={`${menteeFirst} ${menteeLast}`.trim()}
              agreement={agreement}
              onConfirm={() =>
                setAgreement({ ...agreement, mentor_confirmed: true })
              }
            />
          ) : null}

          {activeTab === 'vereinbarung' && !agreement && (
            <>
              <img
                src="/images/placeholder-document.png"
                alt={t(
                  'mentorDashboard.noAgreementAlt',
                  'Noch keine Vereinbarung'
                )}
                className="w-20 h-20 mb-4 opacity-50"
              />
              <div className="text-gray-400">
                {t(
                  'mentorDashboard.noAgreement',
                  'Noch keine bestätigte Vereinbarung vorhanden'
                )}
              </div>
            </>
          )}

          {activeTab === 'feedback' && (
            <div className="w-full mt-2">
              {dueFeedback?.milestone && menteeId && (
                <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-900 text-left w-full rounded">
                  {t(
                    'mentorDashboard.feedbackOpen',
                    'Ein Feedback ist offen zum Meilenstein'
                  )}{' '}
                  <strong>{dueFeedback.milestone}</strong>.{' '}
                  <a
                    href={`#/feedback?uid=${userId}&mentee_id=${menteeId}&milestone=${dueFeedback.milestone}&role=mentor`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-semibold text-yellow-800"
                  >
                    {t('mentorDashboard.fillNow', 'Jetzt ausfüllen')}
                  </a>
                </div>
              )}
              <MentorFeedbackHistory userId={userId} />
            </div>
          )}
        </div>
      </div>

      {/* Chat-Popups */}
      {chatOpen === 'mentee' && menteeId && userId && (
        <ChatPopup
          mentorId={userId}
          menteeId={menteeId}
          senderRole="mentor"
          receiverRole="mentee"
          threadType="pair"
          onClose={() => setChatOpen(null)}
        />
      )}

      {chatOpen === 'hr' && userId && (
        <ChatPopup
          mentorId={userId}
          // WICHTIG: menteeId bewusst null übergeben und HR-Modus erzwingen,
          // damit NICHT der Pair-Thread (Mentee↔Mentor) geladen wird.
          menteeId={null}
          senderRole="mentor"
          receiverRole="hr"
          threadType="hr"
          onClose={() => setChatOpen(null)}
        />
      )}

      {/* Video-Call */}
      {videoOpen && (
        <JitsiMeet
          roomName={
            userId && menteeId
              ? `mentoring-${Math.min(userId, menteeId)}-${Math.max(userId, menteeId)}`
              : `mentoring-${userId}`
          }
          displayName={`${firstName} ${lastName}`.trim()}
          language={jitsiLang}
          matchingId={selectedMatchingId}
          peerUserId={menteeId}
          peerRole="mentee"
          onClose={() => setVideoOpen(false)}
        />
      )}
    </div>
  );
}
